/**
 * Classes Page - Olympic Fit Wellness
 * Browse and filter all fitness classes
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Clock, 
  MapPin, 
  Filter,
  Calendar,
  Flame,
  Users,
  ChevronRight
} from 'lucide-react';
import api from '../lib/api';

export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadClasses();
  }, [activeCategory]);

  const loadClasses = async () => {
    setIsLoading(true);
    try {
      const params = activeCategory !== 'all' ? { category: activeCategory } : {};
      const data = await api.getClasses(params);
      setClasses(data || []);
    } catch (error) {
      console.error('Error loading classes:', error);
      // Fallback data
      setClasses(defaultClasses);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Classes' },
    { id: 'strength', name: 'Strength' },
    { id: 'cardio', name: 'Cardio' },
    { id: 'hiit', name: 'HIIT' },
    { id: 'yoga', name: 'Yoga' },
    { id: 'cycling', name: 'Cycling' },
    { id: 'boxing', name: 'Boxing' },
    { id: 'dance', name: 'Dance' },
    { id: 'recovery', name: 'Recovery' }
  ];

  const defaultClasses = [
    {
      class_id: 'cls_001',
      name: 'Power HIIT',
      category: 'hiit',
      description: 'High-intensity interval training to maximize calorie burn and build endurance.',
      duration_minutes: 45,
      difficulty: 'intermediate',
      calories_estimate: 500,
      image_url: 'https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=600'
    },
    {
      class_id: 'cls_002',
      name: 'Yoga Flow',
      category: 'yoga',
      description: 'Dynamic yoga sequences to build strength, flexibility, and mindfulness.',
      duration_minutes: 60,
      difficulty: 'all_levels',
      calories_estimate: 250,
      image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600'
    },
    {
      class_id: 'cls_003',
      name: 'Power Spin',
      category: 'cycling',
      description: 'High-energy cycling class with climbs, sprints, and rhythm rides.',
      duration_minutes: 45,
      difficulty: 'intermediate',
      calories_estimate: 600,
      image_url: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=600'
    },
    {
      class_id: 'cls_004',
      name: 'Strength & Sculpt',
      category: 'strength',
      description: 'Full-body strength training using weights, resistance bands, and bodyweight.',
      duration_minutes: 50,
      difficulty: 'intermediate',
      calories_estimate: 350,
      image_url: 'https://images.unsplash.com/photo-1581009146145-b5ef050c149a?w=600'
    },
    {
      class_id: 'cls_005',
      name: 'Boxing Fundamentals',
      category: 'boxing',
      description: 'Learn boxing basics while getting an intense cardio workout.',
      duration_minutes: 45,
      difficulty: 'beginner',
      calories_estimate: 450,
      image_url: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=600'
    },
    {
      class_id: 'cls_006',
      name: 'Stretch & Recover',
      category: 'recovery',
      description: 'Gentle stretching and mobility work to aid recovery and prevent injury.',
      duration_minutes: 30,
      difficulty: 'all_levels',
      calories_estimate: 100,
      image_url: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600'
    },
    {
      class_id: 'cls_007',
      name: 'Dance Cardio',
      category: 'dance',
      description: 'Fun, high-energy dance fitness class set to popular music.',
      duration_minutes: 45,
      difficulty: 'all_levels',
      calories_estimate: 400,
      image_url: 'https://images.unsplash.com/photo-1524594152303-9fd13543fe6e?w=600'
    },
    {
      class_id: 'cls_008',
      name: 'Bootcamp',
      category: 'cardio',
      description: 'Military-style workout combining cardio, strength, and agility drills.',
      duration_minutes: 60,
      difficulty: 'advanced',
      calories_estimate: 700,
      image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600'
    }
  ];

  const displayClasses = classes.length > 0 ? classes : defaultClasses;
  const filteredClasses = activeCategory === 'all' 
    ? displayClasses 
    : displayClasses.filter(c => c.category === activeCategory);

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="bg-white" data-testid="classes-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">CLASSES & PROGRAMS</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            500+ Weekly Classes
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            From high-intensity training to restorative yoga, find the perfect class to match your goals.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-neutral-200 py-4">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 text-sm font-heading tracking-[0.06em] whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-black text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Classes Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-12">
                <p className="text-neutral-500 text-base">
                  Showing <span className="font-medium text-neutral-900">{filteredClasses.length}</span> classes
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
                {filteredClasses.map((cls) => (
                  <Link
                    key={cls.class_id}
                    to={`/classes/${cls.class_id}`}
                    className="group"
                    data-testid={`class-card-${cls.class_id}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden mb-4">
                      <img
                        src={cls.image_url || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600'}
                        alt={cls.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-1 text-xs font-medium ${getDifficultyColor(cls.difficulty)}`}>
                        {cls.difficulty?.replace('_', ' ').toUpperCase() || 'ALL LEVELS'}
                      </span>
                      <span className="text-xs text-neutral-500 uppercase tracking-wide">{cls.category}</span>
                    </div>
                    <h3 className="text-lg font-heading font-normal text-neutral-900 mb-2 tracking-wide group-hover:text-neutral-600 transition-colors">
                      {cls.name}
                    </h3>
                    <p className="text-neutral-500 text-sm mb-3 line-clamp-2">{cls.description}</p>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {cls.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Flame className="w-4 h-4" />
                        ~{cls.calories_estimate} cal
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Schedule CTA */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">BOOK YOUR SPOT</span>
              <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
                View Class Schedule
              </h2>
              <p className="text-neutral-600 mb-8 tracking-wide">
                Browse the full weekly schedule, filter by location and time, and book your favorite classes in advance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/portal/schedule"
                  className="inline-flex items-center justify-center gap-2 bg-black text-white font-heading px-8 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-colors"
                >
                  VIEW SCHEDULE <Calendar className="w-4 h-4" />
                </Link>
                <Link
                  to="/membership"
                  className="inline-flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 font-heading px-8 py-4 text-sm tracking-[0.08em] hover:border-black transition-colors"
                >
                  JOIN NOW
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 shadow-sm">
              <h3 className="text-lg font-heading font-normal text-neutral-900 mb-6 tracking-wide">POPULAR THIS WEEK</h3>
              <div className="space-y-4">
                {[
                  { name: 'Power HIIT', time: 'Mon/Wed/Fri 6:00 AM', spots: '3 spots left' },
                  { name: 'Yoga Flow', time: 'Tue/Thu 7:00 AM', spots: '8 spots left' },
                  { name: 'Power Spin', time: 'Daily 5:30 PM', spots: '5 spots left' }
                ].map((cls, idx) => (
                  <div key={idx} className="flex items-center justify-between py-4 border-b border-neutral-100 last:border-0">
                    <div>
                      <h4 className="font-medium text-neutral-900">{cls.name}</h4>
                      <p className="text-sm text-neutral-500">{cls.time}</p>
                    </div>
                    <span className="text-xs font-medium text-orange-600">{cls.spots}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Virtual Classes */}
      <section className="py-16 lg:py-24 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">TRAIN ANYWHERE</span>
          <h2 className="text-3xl md:text-4xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            1,000+ On-Demand Workouts
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10 tracking-wide">
            Can't make it to a class? Access our full library of on-demand workouts anytime, anywhere through the OFW app.
          </p>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 bg-white text-black font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-100 transition-colors"
          >
            GET ACCESS <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
